import { useEffect, useState } from "react";

interface UseSupabaseDataResult<T> {
  /** The live Supabase data, or the fallback if the fetch hasn't
   *  succeeded yet (or Supabase isn't configured yet). */
  data: T;
  /** True while the first fetch attempt is in flight. */
  loading: boolean;
  /** True if `data` came from Supabase. False means we're showing the
   *  placeholder from src/mocks/ — either because Supabase isn't wired
   *  up yet, or a live fetch failed. */
  isLive: boolean;
  error: string | null;
}

/**
 * Fetches data from Supabase via `fetcher`. If the fetch fails for any
 * reason (table doesn't exist yet, env vars not set, RLS blocking the
 * anon key, network error, etc.) this silently falls back to `fallback`
 * so the public site never breaks while Supabase is being connected.
 *
 * Usage:
 *   const { data: season } = useSupabaseData(getActiveSeason, activeSeason);
 *   // `activeSeason` is the mock import — used until Supabase is live.
 */
export function useSupabaseData<T>(
  fetcher: () => Promise<T>,
  fallback: T
): UseSupabaseDataResult<T> {
  const [data, setData] = useState<T>(fallback);
  const [loading, setLoading] = useState(true);
  const [isLive, setIsLive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const result = await fetcher();
        if (!cancelled) {
          setData(result);
          setIsLive(true);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn(
            "[Supabase] Falling back to placeholder data:",
            err instanceof Error ? err.message : err
          );
          setData(fallback);
          setIsLive(false);
          setError(err instanceof Error ? err.message : "Unknown error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
    // Intentionally run once per mount — pages don't currently need to
    // re-fetch on prop changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { data, loading, isLive, error };
}
